import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminPortalLayout from '@/modules/admin/components/AdminPortalLayout';
import { useToast } from '@/hooks/useToast';
import { useAdminQueue } from '@/hooks/queries';

export default function AdminBuildQueuePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showToast, toasts, hideToast } = useToast();
  
  const page = parseInt(searchParams.get('page') || '1', 10);
  
  const { data, isLoading: loading, refetch } = useAdminQueue(page, 50);

  const queue = data?.queue || [];
  const pagination = data?.pagination || {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  };

  const handleRefresh = () => {
    refetch();
  };

  const handlePageChange = (newPage) => {
    navigate(`/admin-portal/queue?page=${newPage}`);
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (workStatus) => {
    if (workStatus === 'in_progress') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
          🔨 In Progress
        </span>
      );
    }
    // needs_work - waiting in queue
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
        ⏳ Waiting
      </span>
    );
  };

  if (loading) {
    return (
      <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading build queue...</p>
          </div>
        </div>
      </AdminPortalLayout>
    );
  }

  return (
    <AdminPortalLayout toasts={toasts} onCloseToast={hideToast}>
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-3xl font-bold text-gray-900">
              📋 Build Queue
            </h1>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
          <p className="text-gray-600">
            {pagination.total === 0 
              ? 'No apps in the build queue' 
              : `${pagination.total} app${pagination.total !== 1 ? 's' : ''} in queue`}
          </p>
        </div>

        {/* Queue Logic Explanation */}
        <div className="bg-white rounded-lg border-2 border-zapt-purple/30 p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📚 Build Queue Logic & Ordering</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">What Apps Are Included?</h3>
              <p className="text-sm text-gray-700 mb-2">
                An app appears in the build queue when its <code className="bg-gray-100 px-1 rounded">workStatus</code> is set to <code className="bg-gray-100 px-1 rounded">needs_work</code> or <code className="bg-gray-100 px-1 rounded">in_progress</code>.
              </p>
              <p className="text-sm text-gray-700 mb-2">
                Apps automatically enter the queue when:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                <li>A new app is submitted and deposit is paid</li>
                <li>A user submits a new feature request on an existing app</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Work Status</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                <li><code className="bg-gray-100 px-1 rounded">needs_work</code> → ⏳ Waiting in queue for admin to start work</li>
                <li><code className="bg-gray-100 px-1 rounded">in_progress</code> → 🔨 Admin is actively working on this app</li>
                <li><code className="bg-gray-100 px-1 rounded">null</code> → Not in queue (no pending work)</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">How Are Apps Ordered?</h3>
              <p className="text-sm text-gray-700 mb-2">
                Apps are ordered <strong>by time only</strong>. We use the <strong>most recent</strong> of (queue entry date, last worked on date) and order ascending (oldest first).
              </p>
              <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-700">
                <ul className="list-disc list-inside ml-4 space-y-1 mb-2">
                  <li><strong>Queue entry date:</strong> <code className="bg-gray-100 px-1 rounded">depositPaidAt</code></li>
                  <li><strong>Last worked on:</strong> <code className="bg-gray-100 px-1 rounded">updatedAt</code></li>
                </ul>
                <p>
                  Apps not worked on recently go to the front; recently worked on apps go to the back.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Queue List */}
        {queue.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-500 text-lg">
              No apps in the build queue.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {queue.map((app, index) => (
              <div
                key={app.appId}
                onClick={() => navigate(`/admin-portal/app/${app.appId}`)}
                className={`bg-white rounded-lg border-2 p-4 transition-shadow hover:shadow-md cursor-pointer ${
                  app.isNext 
                    ? 'border-orange-400 bg-orange-50' 
                    : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 flex items-center gap-4">
                    {/* Queue Position */}
                    <div className="flex-shrink-0">
                      <div className={`text-center px-3 py-2 rounded-lg ${
                        app.isNext 
                          ? 'bg-orange-500 text-white' 
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        <div className="text-lg font-bold">#{app.queuePosition}</div>
                        {app.isNext && (
                          <div className="text-xs font-semibold mt-1">⭐ NEXT</div>
                        )}
                      </div>
                    </div>

                    {/* App Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {app.appName || 'Untitled App'}
                        </h3>
                        {getStatusBadge(app.appWorkStatus)}
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        User: {app.userEmail}
                      </p>
                      {/* Queue Ordering Dates */}
                      <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
                        <div className="text-xs text-gray-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium w-24">Queue Entry:</span>
                            <span>{formatDate(app.queueEntryDate)}</span>
                            <span className="text-gray-400">
                              deposit paid
                            </span>
                          </div>
                          {app.lastWorkedOnDate && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium w-24">Last Worked:</span>
                              <span>{formatDate(app.lastWorkedOnDate)}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 pt-1 border-t border-gray-300">
                            <span className="font-semibold text-zapt-purple w-24">Ordering Date:</span>
                            <span className="font-semibold text-zapt-purple">{formatDate(app.orderingDate)}</span>
                            <span className="text-gray-500 text-xs">
                              (using {app.lastWorkedOnDate && new Date(app.lastWorkedOnDate) > new Date(app.queueEntryDate) ? 'last worked' : 'queue entry'})
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} apps
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={!pagination.hasPrevPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasPrevPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Previous
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                          pagination.page === pageNum
                            ? 'bg-zapt-purple text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={!pagination.hasNextPage}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    pagination.hasNextPage
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminPortalLayout>
  );
}

